import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import {
  CampaignService,
  Campaign as CampaignModel,
  CampaignDailyGoalPayload,
} from "../../services/CampaignService";
import { AuthService } from "../../services/AuthService";
import { UserService, User } from "../../services/UserService";

interface CampaignForm {
  name: string;
  platform_id: string;
  matomo_id: string;
  dashboard_url: string;
  primary_color: string;
  secondary_color: string;
  chatbot_enabled: boolean;
  start_date: string;
  end_date: string;
}

interface CampaignEdit {
  start_date: string;
  end_date: string;
  primary_color: string;
  secondary_color: string;
}

type GoalTemplateMode = "ALL" | "WEEKDAYS" | "WEEKENDS" | "RANGE";

interface GoalTemplate {
  registrations_goal: string;
  coupons_goal: string;
  participants_goal: string;
  apply_mode: GoalTemplateMode;
  range_start: string;
  range_end: string;
}

const roleLabelMap: Record<string, string> = {
  ADMIN: "Administrador",
  USER_HUB: "Usuário Hub",
  USER_CLIENT_ADMIN: "Administrador do cliente",
  USER_CLIENT: "Usuário do cliente",
};

const getRoleLabel = (role?: string | null) => {
  const normalized = AuthService.normalizeRole(role ?? null) ?? role ?? "";
  return roleLabelMap[normalized] || normalized || "Não informado";
};

const Campaigns: React.FC = () => {
  const [form, setForm] = useState<CampaignForm>({
    name: "",
    platform_id: "",
    matomo_id: "",
    dashboard_url: "",
    primary_color: "#0b3d91",
    secondary_color: "#e4e8f0",
    chatbot_enabled: true,
    start_date: "",
    end_date: "",
  });
  const [campaigns, setCampaigns] = useState<CampaignModel[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [assignMessage, setAssignMessage] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Record<number, CampaignEdit>>({});
  const [campaignUsers, setCampaignUsers] = useState<Record<number, User[]>>({});
  const [openCampaign, setOpenCampaign] = useState<number | null>(null);
  const [openGoalsCampaign, setOpenGoalsCampaign] = useState<number | null>(null);
  const [updateMessages, setUpdateMessages] = useState<Record<number, string | null>>({});
  const [updateErrors, setUpdateErrors] = useState<Record<number, string | null>>({});
  const [goalEdits, setGoalEdits] = useState<Record<number, CampaignDailyGoalPayload[]>>({});
  const [goalMessages, setGoalMessages] = useState<Record<number, string | null>>({});
  const [goalErrors, setGoalErrors] = useState<Record<number, string | null>>({});
  const [goalLoading, setGoalLoading] = useState<Record<number, boolean>>({});
  const [goalTemplates, setGoalTemplates] = useState<Record<number, GoalTemplate>>({});
  const [colorEditorsOpen, setColorEditorsOpen] = useState<Record<number, boolean>>({});
  const [goalVisibleCount, setGoalVisibleCount] = useState<Record<number, number>>({});
  const roleOrder = useMemo(() => ["ADMIN", "USER_HUB", "USER_CLIENT_ADMIN", "USER_CLIENT"], []);

  const buildEditState = (campaign: CampaignModel): CampaignEdit => ({
    start_date: formatDateInput(campaign.start_date),
    end_date: formatDateInput(campaign.end_date),
    primary_color: campaign.primary_color || "#0b3d91",
    secondary_color: campaign.secondary_color || "#e4e8f0",
  });

  const normalizeDateForApi = (value: string) => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (match) {
      const [, dd, mm, yyyy] = match;
      return `${yyyy}-${mm}-${dd}`;
    }
    return null;
  };

  const getEditState = (campaignId: number): CampaignEdit => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    const base = campaign ? buildEditState(campaign) : { start_date: "", end_date: "", primary_color: "#0b3d91", secondary_color: "#e4e8f0" };
    return { ...base, ...(editingData[campaignId] || {}) };
  };

  const buildGoalTemplate = (): GoalTemplate => ({
    registrations_goal: "",
    coupons_goal: "",
    participants_goal: "",
    apply_mode: "ALL",
    range_start: "",
    range_end: "",
  });

  const getGoalTemplate = (campaignId: number): GoalTemplate => ({
    ...buildGoalTemplate(),
    ...(goalTemplates[campaignId] || {}),
  });

  const sortUsersByRole = (users: User[]) => {
    return [...users].sort((a, b) => {
      const orderA = roleOrder.indexOf(a.role) === -1 ? Number.MAX_SAFE_INTEGER : roleOrder.indexOf(a.role);
      const orderB = roleOrder.indexOf(b.role) === -1 ? Number.MAX_SAFE_INTEGER : roleOrder.indexOf(b.role);
      if (orderA !== orderB) return orderA - orderB;
      const nameA = a.full_name || a.username;
      const nameB = b.full_name || b.username;
      return nameA.localeCompare(nameB);
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignList, userList] = await Promise.all([CampaignService.list(), UserService.getAllUsers()]);
      setCampaigns(campaignList);
      setUsers(userList);
      const editMap: Record<number, CampaignEdit> = {};
      campaignList.forEach((c) => {
        editMap[c.id] = buildEditState(c);
      });
      setEditingData(editMap);
      if (campaignList.length > 0 && selectedCampaignId === null) {
        setSelectedCampaignId(campaignList[0].id);
      }
      await loadAllCampaignUsers(campaignList);
    } catch (err) {
      setAssignError("Não foi possível carregar campanhas ou usuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (field: keyof CampaignForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (campaignId: number, field: "start_date" | "end_date", value: string) => {
    const base = getEditState(campaignId);
    setEditingData((prev) => ({
      ...prev,
      [campaignId]: {
        ...base,
        [field]: value,
      },
    }));
  };

  const handleColorChange = (campaignId: number, field: "primary_color" | "secondary_color", value: string) => {
    const base = getEditState(campaignId);
    setEditingData((prev) => ({
      ...prev,
      [campaignId]: {
        ...base,
        [field]: value,
      },
    }));
  };

  const normalizeGoalValue = (value: string) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.floor(parsed));
  };

  const parseLocalDate = (value: string) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  };

  const handleTemplateChange = (
    campaignId: number,
    field: keyof GoalTemplate,
    value: string
  ) => {
    setGoalTemplates((prev) => {
      const current = prev[campaignId] ?? buildGoalTemplate();
      return { ...prev, [campaignId]: { ...current, [field]: value } };
    });
  };

  const handleApplyTemplate = (campaignId: number) => {
    setGoalMessages((prev) => ({ ...prev, [campaignId]: null }));
    setGoalErrors((prev) => ({ ...prev, [campaignId]: null }));
    const template = getGoalTemplate(campaignId);
    const registrations = template.registrations_goal.trim()
      ? normalizeGoalValue(template.registrations_goal)
      : null;
    const coupons = template.coupons_goal.trim()
      ? normalizeGoalValue(template.coupons_goal)
      : null;
    const participants = template.participants_goal.trim()
      ? normalizeGoalValue(template.participants_goal)
      : null;

    if (registrations === null && coupons === null && participants === null) {
      setGoalErrors((prev) => ({
        ...prev,
        [campaignId]: "Informe ao menos um valor para aplicar.",
      }));
      return;
    }

    if (template.apply_mode === "RANGE") {
      const rangeStart = parseLocalDate(template.range_start);
      const rangeEnd = parseLocalDate(template.range_end);
      if (!rangeStart || !rangeEnd) {
        setGoalErrors((prev) => ({
          ...prev,
          [campaignId]: "Informe o intervalo de datas para aplicar o modelo.",
        }));
        return;
      }
      if (rangeStart > rangeEnd) {
        setGoalErrors((prev) => ({
          ...prev,
          [campaignId]: "O início do intervalo não pode ser após o fim.",
        }));
        return;
      }
    }

    const current = goalEdits[campaignId] || [];
    if (current.length === 0) {
      setGoalErrors((prev) => ({
        ...prev,
        [campaignId]: "Carregue as metas antes de aplicar o modelo.",
      }));
      return;
    }

    const next = current.map((goal) => {
      const goalDate = parseLocalDate(goal.goal_date);
      if (!goalDate) {
        return goal;
      }
      let shouldApply = false;
      switch (template.apply_mode) {
        case "ALL":
          shouldApply = true;
          break;
        case "WEEKDAYS": {
          const day = goalDate.getDay();
          shouldApply = day >= 1 && day <= 5;
          break;
        }
        case "WEEKENDS": {
          const day = goalDate.getDay();
          shouldApply = day === 0 || day === 6;
          break;
        }
        case "RANGE": {
          const rangeStart = parseLocalDate(template.range_start);
          const rangeEnd = parseLocalDate(template.range_end);
          if (!rangeStart || !rangeEnd) {
            shouldApply = false;
          } else {
            shouldApply = goalDate >= rangeStart && goalDate <= rangeEnd;
          }
          break;
        }
        default:
          shouldApply = true;
      }
      if (!shouldApply) {
        return goal;
      }
      return {
        ...goal,
        registrations_goal: registrations ?? goal.registrations_goal,
        coupons_goal: coupons ?? goal.coupons_goal,
        participants_goal: participants ?? goal.participants_goal,
      };
    });

    setGoalEdits((prev) => ({ ...prev, [campaignId]: next }));
    setGoalMessages((prev) => ({
      ...prev,
      [campaignId]: "Modelo aplicado aos dias selecionados.",
    }));
  };

  const loadCampaignGoals = async (campaign: CampaignModel) => {
    if (!campaign.start_date || !campaign.end_date) {
      setGoalEdits((prev) => ({ ...prev, [campaign.id]: [] }));
      return;
    }
    setGoalLoading((prev) => ({ ...prev, [campaign.id]: true }));
    setGoalErrors((prev) => ({ ...prev, [campaign.id]: null }));
    setGoalMessages((prev) => ({ ...prev, [campaign.id]: null }));
    try {
      const goals = await CampaignService.getDailyGoals(campaign.id);
      const payload = goals.map((goal) => ({
        goal_date: goal.goal_date,
        registrations_goal: goal.registrations_goal ?? 0,
        coupons_goal: goal.coupons_goal ?? 0,
        participants_goal: goal.participants_goal ?? 0,
      }));
      setGoalEdits((prev) => ({ ...prev, [campaign.id]: payload }));
      setGoalVisibleCount((prev) => ({ ...prev, [campaign.id]: 10 }));
    } catch (err) {
      console.error(`Erro ao carregar metas da campanha ${campaign.id}`, err);
      setGoalErrors((prev) => ({ ...prev, [campaign.id]: "Não foi possível carregar as metas diárias." }));
    } finally {
      setGoalLoading((prev) => ({ ...prev, [campaign.id]: false }));
    }
  };

  const handleGoalChange = (
    campaignId: number,
    goalDate: string,
    field: keyof CampaignDailyGoalPayload,
    value: string
  ) => {
    const nextValue = normalizeGoalValue(value);
    setGoalEdits((prev) => {
      const current = prev[campaignId] || [];
      const updated = current.map((goal) =>
        goal.goal_date === goalDate ? { ...goal, [field]: nextValue } : goal
      );
      return { ...prev, [campaignId]: updated };
    });
  };

  const handleSaveGoals = async (campaignId: number) => {
    setGoalMessages((prev) => ({ ...prev, [campaignId]: null }));
    setGoalErrors((prev) => ({ ...prev, [campaignId]: null }));
    const payload = goalEdits[campaignId] || [];
    if (payload.length === 0) {
      setGoalErrors((prev) => ({ ...prev, [campaignId]: "Não há metas para salvar." }));
      return;
    }
    setGoalLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const updated = await CampaignService.updateDailyGoals(campaignId, payload);
      const mapped = updated.map((goal) => ({
        goal_date: goal.goal_date,
        registrations_goal: goal.registrations_goal ?? 0,
        coupons_goal: goal.coupons_goal ?? 0,
        participants_goal: goal.participants_goal ?? 0,
      }));
      setGoalEdits((prev) => ({ ...prev, [campaignId]: mapped }));
      setGoalMessages((prev) => ({ ...prev, [campaignId]: "Metas salvas com sucesso." }));
    } catch (err) {
      console.error(err);
      if (axios.isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        setGoalErrors((prev) => ({ ...prev, [campaignId]: detail || "Erro ao salvar metas diárias." }));
      } else {
        setGoalErrors((prev) => ({ ...prev, [campaignId]: "Erro ao salvar metas diárias." }));
      }
    } finally {
      setGoalLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormMessage(null);
    setLoading(true);
    try {
      await CampaignService.create({
        name: form.name,
        platform_id: form.platform_id,
        matomo_id: form.matomo_id,
        dashboard_url: form.dashboard_url,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        chatbot_enabled: form.chatbot_enabled,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      });
      setForm({
        name: "",
        platform_id: "",
        matomo_id: "",
        dashboard_url: "",
        primary_color: "#0b3d91",
        secondary_color: "#e4e8f0",
        chatbot_enabled: true,
        start_date: "",
        end_date: "",
      });
      setFormMessage("Campanha criada com sucesso.");
      await loadData();
    } catch (err) {
      console.error(err);
      setFormError("Erro ao criar campanha.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDates = async (campaignId: number) => {
    const edit = getEditState(campaignId);
    setAssignError(null);
    setAssignMessage(null);
    setUpdateErrors((prev) => ({ ...prev, [campaignId]: null }));
    setUpdateMessages((prev) => ({ ...prev, [campaignId]: null }));
    setLoading(true);
    try {
      const payload = {
        start_date: normalizeDateForApi(edit.start_date),
        end_date: normalizeDateForApi(edit.end_date),
        primary_color: edit.primary_color,
        secondary_color: edit.secondary_color,
      };
      const updated = await CampaignService.updateDates(campaignId, {
        ...payload,
      });
      const merged = {
        ...updated,
        primary_color: edit.primary_color,
        secondary_color: edit.secondary_color,
      };
      setCampaigns((prev) => prev.map((c) => (c.id === campaignId ? merged : c)));
      setEditingData((prev) => ({
        ...prev,
        [campaignId]: buildEditState(merged),
      }));
      setUpdateMessages((prev) => ({ ...prev, [campaignId]: "Salvo com sucesso." }));
      await loadCampaignGoals(merged);
    } catch (err) {
      console.error(err);
      if (axios.isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        setUpdateErrors((prev) => ({ ...prev, [campaignId]: detail || "Erro ao salvar datas/cores." }));
      } else {
        setUpdateErrors((prev) => ({ ...prev, [campaignId]: "Erro ao salvar datas/cores." }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCampaignId) return;
    if (selectedUsers.length === 0) {
      setAssignError("Selecione ao menos um usuário para vincular.");
      return;
    }
    setAssignError(null);
    setAssignMessage(null);
    setLoading(true);
    try {
      await CampaignService.assignUsers(selectedCampaignId, selectedUsers);
      setAssignMessage("Usuários vinculados à campanha.");
    } catch (err) {
      console.error(err);
      if (axios.isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
        setAssignError(detail || "Erro ao vincular usuários.");
      } else {
        setAssignError("Erro ao vincular usuários.");
      }
    } finally {
      setLoading(false);
    }
  };

  const campaignName = useMemo(
    () => campaigns.find((c) => c.id === selectedCampaignId)?.name ?? "Campanha",
    [campaigns, selectedCampaignId]
  );

  const loadAllCampaignUsers = async (campaignList: CampaignModel[]) => {
    try {
      const results = await Promise.all(
        campaignList.map(async (campaign) => {
          try {
            const usersByCampaign = await CampaignService.getUsers(campaign.id);
            return { campaignId: campaign.id, users: sortUsersByRole(usersByCampaign) };
          } catch (err) {
            console.error(`Erro ao carregar usuários da campanha ${campaign.id}`, err);
            return { campaignId: campaign.id, users: [] as User[] };
          }
        })
      );
      setCampaignUsers((prev) => {
        const next = { ...prev };
        results.forEach(({ campaignId, users }) => {
          next[campaignId] = users;
        });
        return next;
      });
    } catch (err) {
      console.error("Erro ao carregar usuários das campanhas", err);
    }
  };

  const loadCampaignUsers = async (campaignId: number) => {
    try {
      const usersByCampaign = await CampaignService.getUsers(campaignId);
      const sorted = sortUsersByRole(usersByCampaign);
      setCampaignUsers((prev) => ({ ...prev, [campaignId]: sorted }));
    } catch (err) {
      console.error("Erro ao carregar usuários da campanha", err);
      setAssignError("Não foi possível carregar os usuários vinculados a esta campanha.");
    }
  };

  const filteredUsers = useMemo(() => {
    const term = userSearch.toLowerCase();
    return users
      .filter((u) => !selectedUsers.includes(u.id))
      .filter((u) => {
        const name = u.full_name || u.username;
        return name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term);
      })
      .slice(0, 8);
  }, [users, userSearch, selectedUsers]);

  return (
    <AdminLayout>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8 text-slate-900">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">Campanhas</h1>
          <p className="text-base text-slate-600">Cadastre novas campanhas e associe clientes a elas.</p>
        </header>

        <div className="flex flex-col gap-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_20px_45px_rgba(15,23,42,0.15)]">
            <div className="space-y-1 pb-3">
              <h3 className="text-xl font-semibold text-slate-900">Nova campanha</h3>
              <p className="text-sm text-slate-500">Preencha os dados básicos para criar uma campanha.</p>
            </div>
            <form className="mt-3 flex flex-col gap-4" onSubmit={handleSubmit}>
              <label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                Nome da campanha
                <input
                  className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Ex.: Campanha Verão"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                Platform ID
                <input
                  className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                  value={form.platform_id}
                  onChange={(e) => handleChange("platform_id", e.target.value)}
                  placeholder="Identificador interno"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                Matomo ID
                <input
                  className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                  value={form.matomo_id}
                  onChange={(e) => handleChange("matomo_id", e.target.value)}
                  placeholder="ID do Matomo/analytics"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                URL do dashboard
                <input
                  className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                  value={form.dashboard_url}
                  onChange={(e) => handleChange("dashboard_url", e.target.value)}
                  placeholder="https://..."
                  required
                />
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                  <span>Início da campanha</span>
                  <input
                    className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => handleChange("start_date", e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                  <span>Fim da campanha</span>
                  <input
                    className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => handleChange("end_date", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                  <span>Cor primária</span>
                  <input
                    className="h-11 w-full cursor-pointer rounded-xl border-none p-0"
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => handleChange("primary_color", e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
                  <span>Cor secundária</span>
                  <input
                    className="h-11 w-full cursor-pointer rounded-xl border-none p-0"
                    type="color"
                    value={form.secondary_color}
                    onChange={(e) => handleChange("secondary_color", e.target.value)}
                  />
                </div>
                <label className="flex select-none items-center gap-3 text-sm font-semibold text-slate-700">
                  Ativar chatbot por padrão
                  <div className="relative inline-flex h-6 w-12 items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={form.chatbot_enabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, chatbot_enabled: e.target.checked }))}
                    />
                    <span className="pointer-events-none absolute h-6 w-full rounded-full bg-slate-300 transition peer-checked:bg-gradient-to-r peer-checked:from-[#0b3d91] peer-checked:to-[#1e68ff]" />
                    <span className="absolute inset-y-0 left-1 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-6" />
                  </div>
                </label>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-fit rounded-xl bg-gradient-to-r from-[#0b3d91] to-[#1e68ff] px-6 py-2 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition hover:-translate-y-px"
              >
                {loading ? "Salvando..." : "Salvar campanha"}
              </button>
              {formMessage && <p className="mt-2 text-sm font-semibold text-emerald-600">{formMessage}</p>}
              {formError && <p className="mt-2 text-sm font-semibold text-rose-600">{formError}</p>}
            </form>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_20px_45px_rgba(15,23,42,0.15)]">
            <div className="space-y-1 pb-3">
              <h3 className="text-xl font-semibold text-slate-900">Vincular clientes</h3>
              <p className="text-sm text-slate-500">Escolha uma campanha e vincule usuários/empresas a ela.</p>
            </div>
            <div className="space-y-4">
              <label className="flex flex-col gap-2 text-sm font-semibold text-slate-700">
                Campanha
                <select
                  className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                  value={selectedCampaignId ?? ""}
                  onChange={(e) => setSelectedCampaignId(Number(e.target.value))}
                >
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">Usuários</label>
                <input
                  className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 focus:border-slate-500 focus:outline-none"
                  type="text"
                  placeholder="Buscar usuário por nome ou e-mail"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                {userSearch && (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-inner">
                    {filteredUsers.length === 0 && (
                      <div className="text-sm font-semibold text-slate-500">Nenhum usuário encontrado.</div>
                    )}
                    {filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-slate-400"
                        onClick={() => {
                          setSelectedUsers((prev) => [...prev, u.id]);
                          setUserSearch("");
                        }}
                      >
                        <div>
                          <p className="font-semibold text-slate-900">{u.full_name || u.username}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                          {getRoleLabel(u.role)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map((id) => {
                      const u = users.find((user) => user.id === id);
                      if (!u) return null;
                      return (
                        <span
                          key={u.id}
                          className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-900"
                        >
                          <span>
                            {u.full_name || u.username} <small className="font-normal text-slate-500">({getRoleLabel(u.role)})</small>
                          </span>
                          <button
                            type="button"
                            className="text-lg font-bold leading-none text-slate-500"
                            onClick={() => setSelectedUsers((prev) => prev.filter((sid) => sid !== u.id))}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  className="rounded-2xl bg-gradient-to-r from-[#0b3d91] to-[#1e68ff] px-6 py-2 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition hover:-translate-y-px"
                  onClick={handleAssign}
                  disabled={!selectedCampaignId || loading}
                >
                  {loading ? "Salvando..." : "Vincular usuários"}
                </button>
                <div className="min-h-[1rem] text-sm font-semibold text-emerald-600">
                  {assignMessage}
                </div>
              </div>
            </div>
          </section>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_20px_45px_rgba(15,23,42,0.15)]">
          <div className="space-y-1 pb-3">
            <h3 className="text-xl font-semibold text-slate-900">Campanhas e usuários</h3>
            <p className="text-sm text-slate-500">Veja os usuários vinculados a cada campanha.</p>
          </div>
            <div className="mt-3 flex flex-col gap-4">
            {campaigns.map((c) => {
              const isUsersOpen = openCampaign === c.id;
              const isGoalsOpen = openGoalsCampaign === c.id;
              const isOpen = isUsersOpen || isGoalsOpen;
              const usersForCampaign = campaignUsers[c.id] || [];
              const edit = editingData[c.id] || {
                start_date: formatDateInput(c.start_date),
                end_date: formatDateInput(c.end_date),
                primary_color: c.primary_color || "#0b3d91",
                secondary_color: c.secondary_color || "#e4e8f0",
              };
              const goals = goalEdits[c.id] || [];
              const goalsLoading = goalLoading[c.id] || false;
              const goalsError = goalErrors[c.id];
              const goalsMessage = goalMessages[c.id];
              const template = getGoalTemplate(c.id);
              const hasGoalDates = Boolean(c.start_date && c.end_date);
              const groupedByRole = roleOrder
                .map((role) => ({
                  role,
                  users: usersForCampaign.filter((u) => u.role === role),
                }))
                .filter((group) => group.users.length > 0);
              const remainingRoles = usersForCampaign.filter((u) => !roleOrder.includes(u.role));
              if (remainingRoles.length > 0) {
                groupedByRole.push({ role: "OUTROS", users: remainingRoles });
              }
              const visibleCount = goalVisibleCount[c.id] ?? 10;
              const visibleGoals = goals.slice(0, visibleCount);
              return (
                <div
                  key={`accordion-${c.id}`}
                  className={`rounded-2xl border transition ${isOpen ? "border-blue-300 shadow-lg" : "border-slate-200"} bg-slate-50`}
                >
                  <div className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3 md:grid-cols-[1fr_auto] md:items-center">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-lg font-bold text-slate-900">{c.name}</span>
                      <span className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-500">
                        {usersForCampaign.length} usuário(s)
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-start">
                      <button
                        type="button"
                        className="rounded-full bg-gradient-to-r from-[#0b3d91] to-[#1e68ff] px-4 py-1.5 text-xs font-semibold text-white shadow"
                        aria-expanded={isGoalsOpen}
                        onClick={() => {
                          const nextOpen = isGoalsOpen ? null : c.id;
                          setOpenGoalsCampaign(nextOpen);
                          if (nextOpen) {
                            loadCampaignGoals(c);
                          }
                        }}
                      >
                        {isGoalsOpen ? "Fechar metas" : "Definir metas"}
                      </button>
                      <button
                        type="button"
                        className="rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-900 shadow hover:-translate-y-px"
                        onClick={() => {
                          const nextOpen = isUsersOpen ? null : c.id;
                          setOpenCampaign(nextOpen);
                          if (nextOpen) {
                            loadCampaignUsers(nextOpen);
                          }
                        }}
                      >
                        {isUsersOpen ? "Ocultar usuários" : "Listar usuários"}
                      </button>
                    </div>
                  </div>
                  <div className="px-4 py-4">
                    <div className="grid gap-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                          ID {c.id}
                        </span>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${c.chatbot_enabled ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                          {c.chatbot_enabled ? "Chatbot ativo" : "Chatbot inativo"}
                        </span>
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border border-slate-300"
                            checked={c.chatbot_enabled}
                            onChange={async (e) => {
                              const enabled = e.target.checked;
                              try {
                                setLoading(true);
                                await CampaignService.setChatbotEnabled(c.id, enabled);
                                await loadData();
                              } catch (err) {
                                setAssignError("Não foi possível atualizar o chatbot da campanha.");
                              } finally {
                                setLoading(false);
                              }
                            }}
                          />
                          Chatbot
                        </label>
                        <div className="flex flex-col items-start gap-2">
                          <button
                            type="button"
                            className="rounded-full border border-slate-300 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-900 shadow-sm"
                            onClick={() =>
                              setColorEditorsOpen((prev) => ({
                                ...prev,
                                [c.id]: !prev[c.id],
                              }))
                            }
                          >
                            Cores da campanha
                          </button>
                          {colorEditorsOpen[c.id] && (
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-1 text-xs font-semibold text-slate-600">
                                Prim.
                                <input
                                  className="h-10 w-10 cursor-pointer appearance-none rounded-xl border border-slate-200 p-0"
                                  type="color"
                                  value={edit.primary_color}
                                  onChange={(e) => handleColorChange(c.id, "primary_color", e.target.value)}
                                />
                              </label>
                              <label className="flex items-center gap-1 text-xs font-semibold text-slate-600">
                                Sec.
                                <input
                                  className="h-10 w-10 cursor-pointer appearance-none rounded-xl border border-slate-200 p-0"
                                  type="color"
                                  value={edit.secondary_color}
                                  onChange={(e) => handleColorChange(c.id, "secondary_color", e.target.value)}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid gap-3">
                        <div className="grid gap-1 text-xs font-semibold text-slate-700">
                          <span>Início</span>
                          <input
                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                            type="date"
                            value={edit.start_date || ""}
                            onChange={(e) => handleDateChange(c.id, "start_date", e.target.value)}
                          />
                        </div>
                        <div className="grid gap-1 text-xs font-semibold text-slate-700">
                          <span>Fim</span>
                          <input
                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                            type="date"
                            value={edit.end_date || ""}
                            onChange={(e) => handleDateChange(c.id, "end_date", e.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          className="rounded-xl border border-transparent bg-gradient-to-r from-[#0b3d91] to-[#1e68ff] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-lg transition hover:-translate-y-px"
                          onClick={() => handleSaveDates(c.id)}
                          disabled={loading}
                        >
                          Salvar atualizações
                        </button>
                        {(updateMessages[c.id] || updateErrors[c.id]) && (
                          <span className={`text-xs font-semibold ${updateErrors[c.id] ? "text-rose-600" : "text-emerald-600"}`}>
                            {updateErrors[c.id] || updateMessages[c.id]}
                          </span>
                        )}
                      </div>
                    </div>
                    {(isGoalsOpen || isUsersOpen) && (
                      <div className="mt-5 space-y-4">
                        {isGoalsOpen && (
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <h4 className="text-lg font-semibold text-slate-900">Metas diárias da campanha</h4>
                                <p className="text-sm text-slate-500">Defina cadastros, cupons e participantes para cada dia.</p>
                              </div>
                              <button
                                className="rounded-2xl bg-gradient-to-r from-[#0b3d91] to-[#1e68ff] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow transition hover:-translate-y-px"
                                onClick={() => handleSaveGoals(c.id)}
                                disabled={goalsLoading || !hasGoalDates}
                              >
                                {goalsLoading ? "Processando..." : "Salvar metas"}
                              </button>
                            </div>
                            {hasGoalDates ? (
                              <>
                                <div className="mt-3 grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-inner">
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                                      Cadastros
                                      <input
                                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                                        type="number"
                                        min={0}
                                        value={template.registrations_goal}
                                        placeholder="Ex.: 120"
                                        onChange={(e) =>
                                          handleTemplateChange(c.id, "registrations_goal", e.target.value)
                                        }
                                      />
                                    </label>
                                    <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                                      Cupons
                                      <input
                                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                                        type="number"
                                        min={0}
                                        value={template.coupons_goal}
                                        placeholder="Ex.: 80"
                                        onChange={(e) =>
                                          handleTemplateChange(c.id, "coupons_goal", e.target.value)
                                        }
                                      />
                                    </label>
                                    <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                                      Participantes
                                      <input
                                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                                        type="number"
                                        min={0}
                                        value={template.participants_goal}
                                        placeholder="Ex.: 95"
                                        onChange={(e) =>
                                          handleTemplateChange(c.id, "participants_goal", e.target.value)
                                        }
                                      />
                                    </label>
                                  </div>
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                                      Aplicar em
                                      <select
                                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                                        value={template.apply_mode}
                                        onChange={(e) =>
                                          handleTemplateChange(
                                            c.id,
                                            "apply_mode",
                                            e.target.value as GoalTemplateMode
                                          )
                                        }
                                      >
                                        <option value="ALL">Todos os dias</option>
                                        <option value="WEEKDAYS">Dias úteis</option>
                                        <option value="WEEKENDS">Fins de semana</option>
                                        <option value="RANGE">Intervalo de datas</option>
                                      </select>
                                    </label>
                                    {template.apply_mode === "RANGE" && (
                                      <div className="grid gap-2">
                                        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                                          De
                                          <input
                                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                                            type="date"
                                            value={template.range_start}
                                            onChange={(e) =>
                                              handleTemplateChange(c.id, "range_start", e.target.value)
                                            }
                                          />
                                        </label>
                                        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
                                          Até
                                          <input
                                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none"
                                            type="date"
                                            value={template.range_end}
                                            onChange={(e) =>
                                              handleTemplateChange(c.id, "range_end", e.target.value)
                                            }
                                          />
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-900 shadow transition hover:-translate-y-px"
                                    type="button"
                                    onClick={() => handleApplyTemplate(c.id)}
                                  >
                                    Aplicar modelo
                                  </button>
                                  <p className="text-xs font-semibold text-slate-500">Campos vazios mantêm o valor atual.</p>
                                </div>
                                <div className="goals-table mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-inner">
                                  <div className="grid grid-cols-[1.2fr_repeat(3,minmax(90px,1fr))] gap-3 border-b border-slate-200 pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                    <span>Data</span>
                                    <span className="text-center">Cadastros</span>
                                    <span className="text-center">Cupons</span>
                                    <span className="text-center">Participantes</span>
                                  </div>
                                  {visibleGoals.map((goal) => (
                                    <div
                                      key={`${c.id}-${goal.goal_date}`}
                                      className="grid grid-cols-[1.2fr_repeat(3,minmax(90px,1fr))] items-center gap-3 border-b border-slate-100 py-2 text-sm"
                                    >
                                      <span className="font-semibold text-slate-900">{formatDateLabel(goal.goal_date)}</span>
                                      <input
                                        className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-center text-xs font-semibold focus:border-slate-400 focus:outline-none"
                                        type="number"
                                        min={0}
                                        value={goal.registrations_goal}
                                        onChange={(e) =>
                                          handleGoalChange(
                                            c.id,
                                            goal.goal_date,
                                            "registrations_goal",
                                            e.target.value
                                          )
                                        }
                                      />
                                      <input
                                        className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-center text-xs font-semibold focus:border-slate-400 focus:outline-none"
                                        type="number"
                                        min={0}
                                        value={goal.coupons_goal}
                                        onChange={(e) =>
                                          handleGoalChange(c.id, goal.goal_date, "coupons_goal", e.target.value)
                                        }
                                      />
                                      <input
                                        className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-center text-xs font-semibold focus:border-slate-400 focus:outline-none"
                                        type="number"
                                        min={0}
                                        value={goal.participants_goal}
                                        onChange={(e) =>
                                          handleGoalChange(c.id, goal.goal_date, "participants_goal", e.target.value)
                                        }
                                      />
                                    </div>
                                  ))}
                                  {visibleCount < goals.length && (
                                    <div className="flex justify-end">
                                      <button
                                        type="button"
                                        className="mt-3 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow"
                                        onClick={() =>
                                          setGoalVisibleCount((prev) => ({
                                            ...prev,
                                            [c.id]: visibleCount + 10,
                                          }))
                                        }
                                      >
                                        Carregar mais
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {goals.length === 0 && goalsLoading && (
                                  <p className="text-xs font-semibold text-slate-500">Carregando metas...</p>
                                )}
                                {goals.length === 0 && !goalsLoading && !goalsError && (
                                  <p className="text-xs font-semibold text-slate-500">Nenhuma meta diária cadastrada.</p>
                                )}
                              </>
                            ) : (
                              <p className="text-xs font-semibold text-slate-500">Defina início e fim da campanha para liberar as metas diárias.</p>
                            )}
                            {(goalsMessage || goalsError) && (
                              <p className={`text-xs font-semibold ${goalsError ? "text-rose-600" : "text-emerald-600"}`}>
                                {goalsError || goalsMessage}
                              </p>
                            )}
                          </div>
                        )}
                        {isUsersOpen && (
                          <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-inner">
                            {usersForCampaign.length === 0 && (
                              <p className="text-xs font-semibold text-slate-500">Nenhum usuário vinculado.</p>
                            )}
                            {groupedByRole.map((group) => (
                              <div key={`${c.id}-${group.role}`} className="space-y-2 border-t border-slate-100 pt-3">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">{getRoleLabel(group.role)}</p>
                                <ul className="space-y-2">
                                  {group.users.map((u) => (
                                    <li key={`u-${u.id}`} className="grid grid-cols-[1.8fr_auto_auto] items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm shadow-sm">
                                      <span className="font-semibold text-slate-900">{u.full_name || u.username}</span>
                                      <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${u.is_active ? "text-emerald-600" : "text-rose-600"}`}>
                                        {u.is_active ? "Ativo" : "Inativo"}
                                      </span>
                                      <span className="text-right text-xs text-slate-500">{u.email}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        {assignError && (
          <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40">
            <div className="rounded-2xl bg-white p-6 text-center shadow-xl">
              <p className="text-lg font-bold text-rose-600">Não foi possível concluir</p>
              <p className="mt-2 text-sm text-slate-600">{assignError}</p>
              <button
                type="button"
                className="mt-4 rounded-2xl bg-gradient-to-r from-[#0b3d91] to-[#1e68ff] px-6 py-2 text-sm font-semibold uppercase text-white"
                onClick={() => setAssignError(null)}
              >
                Voltar
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Campaigns;

function formatDateInput(dateValue?: string | null): string {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDateLabel(dateValue?: string | null): string {
  if (!dateValue) return "-";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-");
    return `${day}/${month}/${year}`;
  }
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return String(dateValue);
  return d.toLocaleDateString("pt-BR");
}
